import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { BASE_URL } from "../../constants";

const Timeline = ({ leadId, leadName, leadCompany }) => {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [limit, setLimit] = useState(50);

  const token =
    useSelector((state) => state.auth.token) || localStorage.getItem("token");

  const fetchTimeline = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${BASE_URL}/timeline/lead/${leadId}?limit=${limit}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch timeline");
      }

      const data = await response.json();
      setTimeline(data.data.timeline || []);
    } catch (error) {
      console.error("Error fetching timeline:", error);
      setError(error.message);
      toast.error("Failed to load timeline", {
        containerId: "main-toast-container",
      });
    } finally {
      setLoading(false);
    }
  }, [leadId, limit, token]);

  useEffect(() => {
    if (leadId) {
      fetchTimeline();
    }
  }, [leadId, fetchTimeline]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getActionIcon = (action) => {
    switch (action) {
      case "created":
        return "🎯"; // Target for new lead
      case "contacted":
        return "📞"; // Phone for contact
      case "status_changed":
        return "🔄"; // Cycle for status change
      case "updated":
        return "✏️"; // Pencil for edit
      case "note_added":
        return "📝"; // Note for notes
      case "score_updated":
        return "📊"; // Chart for score
      case "viewed":
        return "👁️"; // Eye for view
      default:
        return "📋"; // Clipboard for general
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case "created":
        return "bg-green-100 text-green-800 border-green-200";
      case "contacted":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "status_changed":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "updated":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "note_added":
        return "bg-indigo-100 text-indigo-800 border-indigo-200";
      case "score_updated":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "viewed":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const renderTimelineItem = (item, index) => {
    const isLast = index === timeline.length - 1;

    return (
      <div key={item._id} className="relative flex items-start space-x-3 pb-4">
        {/* Timeline line */}
        {!isLast && (
          <div className="absolute left-4 top-10 w-0.5 h-full bg-gray-200"></div>
        )}

        {/* Icon */}
        <div
          className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border ${getActionColor(
            item.action
          )}`}
        >
          {getActionIcon(item.action)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-900">
                {item.adminName || item.adminId?.name || "System"}
              </span>
              <span className="text-xs text-gray-500">
                {formatDate(item.createdAt)}
              </span>
            </div>
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getActionColor(
                item.action
              )}`}
            >
              {item.action.replace("_", " ").toUpperCase()}
            </span>
          </div>

          <p className="text-sm text-gray-700 mt-1">{item.description}</p>

          {/* Show field changes if available */}
          {item.field &&
            (item.oldValue !== undefined || item.newValue !== undefined) && (
              <div className="mt-2 text-xs text-gray-600 bg-gray-50 rounded p-2">
                <div className="font-medium">Field: {item.field}</div>
                {item.oldValue !== undefined && (
                  <div>
                    <span className="text-red-600">From:</span>{" "}
                    {item.oldValue === null ? "None" : String(item.oldValue)}
                  </div>
                )}
                {item.newValue !== undefined && (
                  <div>
                    <span className="text-green-600">To:</span>{" "}
                    {item.newValue === null ? "None" : String(item.newValue)}
                  </div>
                )}
              </div>
            )}

          {/* Show metadata if available */}
          {item.metadata && Object.keys(item.metadata).length > 0 && (
            <div className="mt-2 text-xs text-gray-500">
              {item.metadata.source && (
                <span className="inline-block mr-2">
                  Source: {item.metadata.source}
                </span>
              )}
              {item.metadata.scoreDifference && (
                <span className="inline-block mr-2">
                  Score change: {item.metadata.scoreDifference > 0 ? "+" : ""}
                  {item.metadata.scoreDifference}
                </span>
              )}
              {item.metadata.changeType && (
                <span className="inline-block mr-2">
                  Type: {item.metadata.changeType}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <span className="ml-2 text-gray-600">Loading timeline...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8">
        <div className="text-red-600 mb-4">Error loading timeline: {error}</div>
        <button
          onClick={fetchTimeline}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="timeline-container">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Lead Timeline</h3>
          <p className="text-sm text-gray-600">
            Activity history for {leadName} ({leadCompany})
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <label className="text-sm text-gray-600">Show:</label>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          >
            <option value={25}>Last 25</option>
            <option value={50}>Last 50</option>
            <option value={100}>Last 100</option>
          </select>
        </div>
      </div>

      {/* Timeline */}
      <div className="space-y-0">
        {timeline.length === 0 ? (
          <div className="text-center p-8 text-gray-500">
            <div className="text-4xl mb-2">📋</div>
            <p>No timeline entries found for this lead.</p>
          </div>
        ) : (
          timeline.map((item, index) => renderTimelineItem(item, index))
        )}
      </div>

      {/* Footer */}
      {timeline.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200 text-center text-sm text-gray-500">
          Showing {timeline.length} timeline entries
          {timeline.length >= limit && (
            <div className="mt-2">
              <button
                onClick={() => setLimit(limit + 25)}
                className="text-blue-600 hover:text-blue-800"
              >
                Load more entries
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Timeline;
